import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Paper,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getAuthHeaders } from '../../../utils/auth';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import {
  monthNames,
  formatDuration,
  formatMonths,
  formatPurchasePeriod,
  formatEventDate,
  getInitialFormData,
  passTypeToFormData,
  formDataToApiPayload,
  validateFormData,
} from './utils/formatters';

const PassTypeManagement = () => {
  const [passTypes, setPassTypes] = useState([]);
  const [lessonCategories, setLessonCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPassType, setEditingPassType] = useState(null);
  const [viewingPassType, setViewingPassType] = useState(null);
  const [deletingPassType, setDeletingPassType] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());

  useEffect(() => {
    fetchPassTypes();
    fetchLessonCategories();
  }, []);

  useEffect(() => {
    if (createEditDialogOpen && formData.is_event_pass) {
      fetchEvents();
    }
  }, [createEditDialogOpen, formData.is_event_pass]);

  const fetchPassTypes = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      // Use include_all=true to get all pass types regardless of availability (for admin)
      let response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/with-availability-info?include_all=true`, { headers });
      if (!response.ok) {
        response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`, { headers });
      }
      const data = await response.json();
      if (response.ok) {
        setPassTypes(data.objects || []);
        setError('');
      } else {
        setError(response.status === 403 || response.status === 401 ? 'Brak uprawnień do pobrania karnetów' : 'Nie udało się pobrać karnetów');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonCategories = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`, { headers });
      const data = await response.json();
      if (response.ok) setLessonCategories(data.objects || []);
    } catch (err) {
      console.error('Error fetching lesson categories:', err);
    }
  };

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/events?active_only=true`,
        { headers }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableEvents(data || []);
      } else {
        setAvailableEvents([]);
      }
    } catch (err) {
      setAvailableEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleFormChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleMonthToggle = (monthNumber) => {
    setFormData(prev => ({
      ...prev,
      available_months: prev.available_months.includes(monthNumber)
        ? prev.available_months.filter(m => m !== monthNumber)
        : [...prev.available_months, monthNumber]
    }));
  };

  const handleLessonCategoryChange = (categoryId, lessons_to_use) => {
    setFormData(prev => ({
      ...prev,
      lesson_categories_for_pass_type: prev.lesson_categories_for_pass_type.map(cat =>
        cat.lesson_category_id === categoryId ? { ...cat, lessons_to_use } : cat
      )
    }));
  };

  const handleUnlimitedChange = (categoryId, is_unlimited) => {
    setFormData(prev => ({
      ...prev,
      lesson_categories_for_pass_type: prev.lesson_categories_for_pass_type.map(cat =>
        cat.lesson_category_id === categoryId
          ? { ...cat, is_unlimited, lessons_to_use: is_unlimited ? 0 : (cat.lessons_to_use || 1) }
          : cat
      )
    }));
  };

  const toggleLessonCategory = (categoryId) => {
    setFormData(prev => {
      const exists = prev.lesson_categories_for_pass_type.find(cat => cat.lesson_category_id === categoryId);
      return {
        ...prev,
        lesson_categories_for_pass_type: exists
          ? prev.lesson_categories_for_pass_type.filter(cat => cat.lesson_category_id !== categoryId)
          : [...prev.lesson_categories_for_pass_type, { lesson_category_id: categoryId, lessons_to_use: 1, is_unlimited: false }]
      };
    });
  };

  const toggleEventPassEvent = (eventId) => {
    setFormData(prev => ({
      ...prev,
      event_ids_for_event_pass: prev.event_ids_for_event_pass.includes(eventId)
        ? prev.event_ids_for_event_pass.filter(id => id !== eventId)
        : [...prev.event_ids_for_event_pass, eventId]
    }));
  };

  const handleCreate = () => {
    setFormData(getInitialFormData());
    setEditingPassType(null);
    setCreateEditDialogOpen(true);
  };

  const handleEdit = (passType) => {
    setFormData(passTypeToFormData(passType));
    setEditingPassType(passType);
    setCreateEditDialogOpen(true);
  };

  const handleView = (passType) => {
    setViewingPassType(passType);
    setViewDetailsDialogOpen(true);
  };

  const handleDelete = (passType) => {
    setDeletingPassType(passType);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    const validationError = validateFormData(formData);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }
    try {
      const apiData = formDataToApiPayload(formData);
      const isEditing = editingPassType !== null;
      const url = isEditing
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/${editingPassType.id}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`;
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(apiData)
      });
      if (response.ok) {
        setSuccess(isEditing ? 'Karnet zaktualizowany pomyślnie' : 'Karnet utworzony pomyślnie');
        setCreateEditDialogOpen(false);
        await fetchPassTypes();
      } else {
        const errorData = await response.json();
        setError((response.status === 403 || response.status === 401) ? 'Brak uprawnień do zapisania karnetu' : (errorData.detail || 'Błąd podczas zapisywania karnetu'));
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingPassType) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/${deletingPassType.id}`, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        setSuccess('Karnet usunięty pomyślnie');
        setDeleteDialogOpen(false);
        await fetchPassTypes();
      } else {
        setError((response.status === 403 || response.status === 401) ? 'Brak uprawnień do usunięcia karnetu' : 'Nie udało się usunąć karnetu');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
      setDeletingPassType(null);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Nazwa', flex: 1, minWidth: 200 },
    { field: 'price', headerName: 'Cena', width: 100, renderCell: (params) => `${params.value} zł` },
    { field: 'valid_duration', headerName: 'Czas trwania', width: 120, renderCell: (params) => formatDuration(params.value) },
    {
      field: 'is_event_pass', headerName: 'Typ', width: 120,
      renderCell: (params) => params.value
        ? <Chip icon={<EventIcon />} label="Event" color="secondary" size="small" />
        : <Chip label="Standardowy" color="default" size="small" />
    },
    {
      field: 'active', headerName: 'Aktywny', width: 100,
      renderCell: (params) => <Chip label={params.value ? 'Tak' : 'Nie'} color={params.value ? 'success' : 'error'} size="small" />
    },
    {
      field: 'purchase_limit', headerName: 'Limit zakupów', width: 130,
      renderCell: (params) => {
        const availabilityInfo = params.row.availability_info;
        if (availabilityInfo?.has_purchase_limit) {
          const { current_active_count, purchase_limit, is_available } = availabilityInfo;
          return (
            <Box>
              <Typography variant="body2" sx={{ color: is_available ? 'text.primary' : 'error.main', fontWeight: is_available ? 'normal' : 'bold' }}>
                {current_active_count}/{purchase_limit}
              </Typography>
              {!is_available && <Typography variant="caption" color="error">LIMIT</Typography>}
            </Box>
          );
        }
        return <Typography variant="body2">Bez limitu</Typography>;
      }
    },
    {
      field: 'actions', headerName: 'Akcje', width: 150, sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleView(params.row)} size="small"><VisibilityIcon /></IconButton>
          <IconButton onClick={() => handleEdit(params.row)} size="small"><EditIcon /></IconButton>
          <IconButton onClick={() => handleDelete(params.row)} size="small" color="error"><DeleteIcon /></IconButton>
        </Box>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>Zarządzanie Karnetami</Typography>
            <Typography variant="body2" color="text.secondary">
              Tworzenie i edycja typów karnetów z możliwością ograniczenia dostępności do konkretnych miesięcy i okresów
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={{ textTransform: 'none' }}>
            Nowy Karnet
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid rows={passTypes} columns={columns} pageSize={10} rowsPerPageOptions={[5, 10, 25]} loading={loading} disableSelectionOnClick
            sx={{ '& .MuiDataGrid-cell:hover': { backgroundColor: '#f5f5f5' } }} />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={createEditDialogOpen} onClose={() => setCreateEditDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh', overflow: 'hidden' } }}>
          <DialogTitle>{editingPassType ? 'Edytuj Karnet' : 'Nowy Karnet'}</DialogTitle>
          <DialogContent sx={{ overflowY: 'auto' }}>
            <Box sx={{ pt: 2 }}>
              {/* Event Pass Toggle */}
              <Card sx={{ mb: 3, bgcolor: formData.is_event_pass ? 'secondary.light' : 'default' }}>
                <CardContent>
                  <FormControlLabel
                    control={<Checkbox checked={formData.is_event_pass} onChange={(e) => handleFormChange('is_event_pass', e.target.checked)} color="secondary" />}
                    label={<Box display="flex" alignItems="center"><EventIcon sx={{ mr: 1 }} /><Typography variant="h6">Karnet Eventowy (dla konkretnych eventów)</Typography></Box>}
                  />
                  {formData.is_event_pass && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Karnet eventowy jest ważny tylko dla wybranych eventów (np. warsztaty, retreaty). Eventy zarządzasz w zakładce "Zarządzanie Eventami".
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Basic Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Podstawowe informacje</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}><TextField fullWidth label="Nazwa karnetu" value={formData.name} onChange={(e) => handleFormChange('name', e.target.value)} margin="normal" /></Grid>
                    <Grid item xs={6}><TextField fullWidth label="Cena (zł)" type="number" value={formData.price} onChange={(e) => handleFormChange('price', e.target.value)} margin="normal" /></Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth
                        label={formData.is_event_pass ? "Czas trwania (dni) - automatycznie obliczony" : (formData.month_restricted ? "Czas trwania (dni) - opcjonalne" : "Czas trwania (dni)")}
                        type="number" value={formData.valid_duration_days} onChange={(e) => handleFormChange('valid_duration_days', e.target.value)} margin="normal"
                        helperText={formData.is_event_pass ? "Pozostaw puste - zostanie obliczony automatycznie" : (formData.month_restricted ? "Pozostaw puste dla auto-kalkulacji" : "")}
                      />
                    </Grid>
                    <Grid item xs={6}><TextField fullWidth label="Limit zakupów (opcjonalne)" type="number" value={formData.purchase_limit} onChange={(e) => handleFormChange('purchase_limit', e.target.value)} margin="normal" helperText="Pozostaw puste dla nieograniczonej liczby" /></Grid>
                    <Grid item xs={6}><FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => handleFormChange('active', e.target.checked)} />} label="Karnet aktywny (widoczny w cenniku)" /></Grid>
                    <Grid item xs={6}><FormControlLabel control={<Checkbox checked={formData.available_for_purchase} onChange={(e) => handleFormChange('available_for_purchase', e.target.checked)} />} label="Dostępny do zakupu" /></Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Event Pass Events Selection */}
              {formData.is_event_pass && (
                <Card sx={{ mb: 3, border: '2px solid', borderColor: 'secondary.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom><EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Wybierz eventy dla karnetu</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Wybierz eventy, na które ten karnet będzie ważny. Każdy event może zawierać wiele zajęć.
                    </Typography>
                    {loadingEvents ? <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box> : (
                      <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                        <List dense>
                          {availableEvents.length === 0 ? (
                            <ListItem>
                              <ListItemText 
                                primary="Brak dostępnych eventów" 
                                secondary="Utwórz eventy w zakładce 'Zarządzanie Eventami'" 
                              />
                            </ListItem>
                          ) : (
                            availableEvents.map((event) => (
                              <ListItem 
                                key={event.id} 
                                sx={{ 
                                  bgcolor: formData.event_ids_for_event_pass.includes(event.id) ? 'secondary.light' : 'inherit',
                                  borderRadius: 1,
                                  mb: 0.5
                                }}
                              >
                                <Checkbox 
                                  checked={formData.event_ids_for_event_pass.includes(event.id)} 
                                  onChange={() => toggleEventPassEvent(event.id)} 
                                />
                                <ListItemText 
                                  primary={event.name} 
                                  secondary={
                                    <Box>
                                      {event.start_date && event.end_date && (
                                        <Typography variant="caption" component="span">
                                          {formatEventDate(event.start_date)} - {formatEventDate(event.end_date)}
                                        </Typography>
                                      )}
                                      {event.lesson_count > 0 && (
                                        <Chip 
                                          label={`${event.lesson_count} zajęć`} 
                                          size="small" 
                                          sx={{ ml: 1 }} 
                                        />
                                      )}
                                    </Box>
                                  } 
                                />
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Paper>
                    )}
                    {formData.event_ids_for_event_pass.length > 0 && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        Wybrano {formData.event_ids_for_event_pass.length} {formData.event_ids_for_event_pass.length === 1 ? 'event' : 'eventów'}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Month Availability Section - only for regular passes */}
              {!formData.is_event_pass && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center"><CalendarIcon sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6">Ograniczenie dostępności do konkretnych miesięcy</Typography></Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControlLabel control={<Checkbox checked={formData.month_restricted} onChange={(e) => handleFormChange('month_restricted', e.target.checked)} color="primary" />} label="Ograniczyć dostępność do wybranych miesięcy" sx={{ mb: 2 }} />
                    {formData.month_restricted && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Wybierz dostępne miesiące:</Typography>
                        <Grid container spacing={1}>
                          {monthNames.map((monthName, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index + 1}>
                              <FormControlLabel control={<Checkbox size="small" checked={formData.available_months.includes(index + 1)} onChange={() => handleMonthToggle(index + 1)} color="primary" />} label={monthName} />
                            </Grid>
                          ))}
                        </Grid>
                        {formData.available_months.length > 0 && <Alert severity="info" sx={{ mt: 2 }}>Wybrane: {formData.available_months.map(m => monthNames[m - 1]).join(', ')}</Alert>}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Purchase Period Section */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center"><TimeIcon sx={{ mr: 1, color: 'info.main' }} /><Typography variant="h6">Ograniczenie okresu zakupu</Typography></Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControlLabel control={<Checkbox checked={formData.purchase_period_restricted} onChange={(e) => handleFormChange('purchase_period_restricted', e.target.checked)} color="primary" />} label="Ograniczyć okres zakupu" sx={{ mb: 2 }} />
                  {formData.purchase_period_restricted && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Dostępny od" type="datetime-local" value={formData.purchase_available_from} onChange={(e) => handleFormChange('purchase_available_from', e.target.value)} InputLabelProps={{ shrink: true }} margin="normal" /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Dostępny do" type="datetime-local" value={formData.purchase_available_until} onChange={(e) => handleFormChange('purchase_available_until', e.target.value)} InputLabelProps={{ shrink: true }} margin="normal" /></Grid>
                    </Grid>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Lesson Categories Section - only for regular passes */}
              {!formData.is_event_pass && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Kategorie zajęć</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Wybierz kategorie zajęć</Typography>
                    {lessonCategories.map(category => {
                      const isSelected = formData.lesson_categories_for_pass_type.find(cat => cat.lesson_category_id === category.id);
                      return (
                        <Box key={category.id} sx={{ mb: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6}><FormControlLabel control={<Checkbox checked={!!isSelected} onChange={() => toggleLessonCategory(category.id)} color="primary" />} label={category.name} /></Grid>
                            {isSelected && (
                              <>
                                <Grid item xs={3}><FormControlLabel control={<Checkbox checked={isSelected.is_unlimited || false} onChange={(e) => handleUnlimitedChange(category.id, e.target.checked)} color="secondary" size="small" />} label="Bez limitu" /></Grid>
                                <Grid item xs={3}>
                                  {!isSelected.is_unlimited && <TextField fullWidth size="small" label="Liczba lekcji" type="number" value={isSelected.lessons_to_use} onChange={(e) => handleLessonCategoryChange(category.id, parseInt(e.target.value) || 1)} inputProps={{ min: 1 }} />}
                                  {isSelected.is_unlimited && <Typography variant="body2" color="primary" sx={{ mt: 1 }}>Nieograniczone</Typography>}
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateEditDialogOpen(false)} disabled={loading}>Anuluj</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : null}>
              {loading ? 'Zapisywanie...' : (editingPassType ? 'Aktualizuj' : 'Utwórz')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={viewDetailsDialogOpen} onClose={() => setViewDetailsDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Szczegóły Karnetu</Typography>
              <IconButton onClick={() => handleEdit(viewingPassType)} color="primary"><EditIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {viewingPassType && (
              <Box>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Typography variant="h5">{viewingPassType.name}</Typography>
                      {viewingPassType.is_event_pass && <Chip icon={<EventIcon />} label="Event Pass" color="secondary" />}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}><Typography variant="body2" color="text.secondary">Cena</Typography><Typography variant="h6">{viewingPassType.price} zł</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2" color="text.secondary">Czas trwania</Typography><Typography variant="h6">{formatDuration(viewingPassType.valid_duration)}</Typography></Grid>
                      <Grid item xs={6}><Typography variant="body2" color="text.secondary">Status</Typography><Chip label={viewingPassType.active ? 'Aktywny' : 'Nieaktywny'} color={viewingPassType.active ? 'success' : 'error'} /></Grid>
                      <Grid item xs={6}><Typography variant="body2" color="text.secondary">Limit zakupów</Typography><Typography variant="h6">{viewingPassType.purchase_limit ? `Max ${viewingPassType.purchase_limit}` : 'Bez limitu'}</Typography></Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {viewingPassType.is_event_pass && viewingPassType.events_for_event_pass && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom><EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Eventy ({viewingPassType.events_for_event_pass.length})</Typography>
                      <List dense>
                        {viewingPassType.events_for_event_pass.map((event, index) => (
                          <ListItem key={index}>
                            <ListItemText 
                              primary={event.event_name || `Event #${event.event_id}`} 
                              secondary={
                                event.start_date && event.end_date 
                                  ? `${formatEventDate(event.start_date)} - ${formatEventDate(event.end_date)}`
                                  : 'Daty nie określone'
                              } 
                            />
                            {event.lesson_count > 0 && (
                              <Chip label={`${event.lesson_count} zajęć`} size="small" />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}

                {!viewingPassType.is_event_pass && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom><CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Dostępność</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Ograniczenie miesięczne:</Typography>
                        {viewingPassType.month_restricted
                          ? <Alert severity="warning" sx={{ mt: 1 }}>Dostępny tylko w: {formatMonths(viewingPassType.available_months)}</Alert>
                          : <Typography variant="body2">Dostępny przez cały rok</Typography>}
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Okres zakupu:</Typography>
                        {viewingPassType.purchase_period_restricted
                          ? <Alert severity="info" sx={{ mt: 1 }}>{formatPurchasePeriod(viewingPassType)}</Alert>
                          : <Typography variant="body2">Zawsze dostępny do zakupu</Typography>}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {!viewingPassType.is_event_pass && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Kategorie zajęć</Typography>
                      {viewingPassType.lesson_categories_for_pass_type?.length > 0 ? (
                        <Grid container spacing={2}>
                          {viewingPassType.lesson_categories_for_pass_type.map((category, index) => {
                            const categoryName = lessonCategories.find(cat => cat.id === category.lesson_category_id)?.name || `ID: ${category.lesson_category_id}`;
                            return (
                              <Grid item xs={12} sm={6} key={index}>
                                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                  <Typography variant="subtitle1" gutterBottom>{categoryName}</Typography>
                                  <Typography variant="body2" color="text.secondary">{category.is_unlimited ? 'Nieograniczone' : `Lekcji: ${category.lessons_to_use}`}</Typography>
                                  {category.is_unlimited && <Chip label="UNLIMITED" color="success" size="small" sx={{ mt: 1 }} />}
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      ) : <Typography variant="body2" color="text.secondary">Brak przypisanych kategorii</Typography>}
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setViewDetailsDialogOpen(false)}>Zamknij</Button></DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Potwierdź usunięcie</DialogTitle>
          <DialogContent>
            <Typography>Czy na pewno chcesz usunąć karnet "{deletingPassType?.name}"?</Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>Ta operacja jest nieodwracalna.</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>Anuluj</Button>
            <Button color="error" onClick={handleConfirmDelete} disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : null}>{loading ? 'Usuwanie...' : 'Usuń'}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PassTypeManagement;
